import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  spacing,
  typography,
  colors,
} from "../../../../../../../../assets/styles/tokens";
import MissingData from "./MissingData/MissingData";
import ftd from "./FTD/Ftd";
import Brands from "./Brands/Brands";
import Income from "./Income/Income";
import Earnings from "./Earnings/Earnings";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";

const tabConfig = [
  { label: "Missing Data", value: "missing_data", trackerId: "893" },
  { label: "FTD", value: "ftd", trackerId: "897" },
  { label: "Brands", value: "brands", trackerId: "904" },
  { label: "Income", value: "income", trackerId: "910" },
  { label: "Earnings", value: "earnings", trackerId: "916" },
];

const TAB_COMPONENTS = [MissingData, ftd, Brands, Income, Earnings];

interface TabPanelProps {
  children?: React.ReactNode;
  activeIndex: number;
  index: number;
  visited: boolean;
}

// Only renders children if tab has been visited at least once
// Once visited, keeps mounted with display:none (no re-mount, no re-fetch)
const TabPanel = React.memo(
  ({ children, activeIndex, index, visited }: TabPanelProps) => {
    if (!visited) return null; // never visited → don't mount → no API call
    return (
      <Box sx={{ display: activeIndex === index ? "block" : "none", pt: 1 }}>
        {children}
      </Box>
    );
  },
);

export default function AnomaliesTabs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get("subAnomaliesTab");
  const initialIndex = tabConfig.findIndex(
    (tab) => tab.value === currentTabParam,
  );
  const activeIndex = initialIndex !== -1 ? initialIndex : 0;

  const [value, setValue] = React.useState(activeIndex);

  // Track which tabs have been visited — only visited tabs are mounted
  // On refresh: only the active tab index is pre-visited
  const workspaceId = searchParams.get("workspace");
  const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
    () => new Set([activeIndex]),
  );

  // Reset visited tabs when workspace changes
  const prevWorkspaceRef = React.useRef(workspaceId);
  if (prevWorkspaceRef.current !== workspaceId) {
    prevWorkspaceRef.current = workspaceId;
    setVisitedTabs(new Set([value])); // ← runs during render, before effects
  }

  // Keep the URL in sync with the active sub-tab even when it was never
  // explicitly clicked (e.g. landing on Anomalies fresh, with no
  // subAnomaliesTab param yet) — the URL should always reflect real state.
  React.useEffect(() => {
    if (!searchParams.get("subAnomaliesTab")) {
      searchParams.set("subAnomaliesTab", tabConfig[activeIndex].value);
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Mark this tab as visited so it mounts for the first time
    setVisitedTabs((prev) => new Set(prev).add(newValue));
    const newTabValue = tabConfig[newValue].value;
    searchParams.set("subAnomaliesTab", newTabValue);
    setSearchParams(searchParams);
  };

  const {
    fetchMissingData,
    fetchClicksWithFtdData,
    fetchProblematicFtdData,
    fetchWithoutFtdAndNetRevData,
    fetchGapsBetweenPlayersData,
    fetchIncomeGapsData,
    fetchDecreaseDailyIncomeData,
    fetchDailyEarningGapsData,
    fetchMonthlyEarningGapsData,
    getSiteFromWorkspace,
  } = useBizDevelopmentDashboardMobile();
  const loaderMissingData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderMissingData,
  );
  const loaderFtdData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderFtdData,
  );
  const loaderAnomaliesBrand = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderAnomaliesBrand,
  );
  const loaderIncomeData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderIncomeData,
  );
  const loaderEarningData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderEarningData,
  );

  const isRefreshable =
    value === 0 || value === 1 || value === 2 || value === 3 || value === 4;
  const isRefreshing =
    value === 0
      ? loaderMissingData
      : value === 1
        ? loaderFtdData
        : value === 2
          ? loaderAnomaliesBrand
          : value === 3
            ? loaderIncomeData
            : value === 4
              ? loaderEarningData
              : false;

  const handleRefresh = () => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    if (value === 0) {
      fetchMissingData(sites);
      return;
    }
    if (value === 1) {
      // Default (no subFtdTab yet) is the FTD tab's first sub-tab.
      const activeFtdSubTab =
        searchParams.get("subFtdTab") || "ClicksWithFtd";
      if (activeFtdSubTab === "ProblematicFtds") {
        fetchProblematicFtdData(sites);
      } else {
        fetchClicksWithFtdData(sites);
      }
      return;
    }
    if (value === 2) {
      // Default (no subBrandsTab yet) is the Brands tab's first sub-tab.
      const activeBrandsSubTab =
        searchParams.get("subBrandsTab") || "WithoutFtd";
      if (activeBrandsSubTab === "NoPlayer") {
        fetchGapsBetweenPlayersData(sites);
      } else {
        fetchWithoutFtdAndNetRevData(sites);
      }
      return;
    }
    if (value === 3) {
      // Default (no subIncomeTab yet) is the Income tab's first sub-tab.
      const activeIncomeSubTab =
        searchParams.get("subIncomeTab") || "IncomeGaps";
      if (activeIncomeSubTab === "DecreaseDailyIncome") {
        fetchDecreaseDailyIncomeData(sites);
      } else {
        fetchIncomeGapsData(sites);
      }
      return;
    }
    if (value === 4) {
      // Default (no subEarningsTab yet) is the Earnings tab's first sub-tab.
      const activeEarningsSubTab =
        searchParams.get("subEarningsTab") || "DailyEarningsGaps";
      if (activeEarningsSubTab === "MonthlyEarningsGaps") {
        fetchMonthlyEarningGapsData(sites);
      } else {
        fetchDailyEarningGapsData(sites);
      }
    }
  };

  return (
    <Box sx={{ height: "93vh", overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minHeight: spacing.Spacing["3xl"].$value,
            px: spacing.Radius["2xl"].$value,
            pb: spacing.Radius.md.$value,
            boxShadow: "0px 2px 5px -3px" + colors.GrayDarkMode[400].$value,
            "& .MuiTabs-scrollButtons": {
              color: colors.GrayDarkMode[600].$value,
            },
          }}
          TabIndicatorProps={{
            sx: {
              height: spacing.Spacing.none.$value,
            },
          }}
          slotProps={{
            list: {
              sx: {
                justifyContent: "flex-start",
              },
            },
          }}
        >
          {tabConfig.map((tab, index) => (
            <Tab
              key={tab.label}
              // disabled={loadingTrendsMobData}
              label={tab.label}
              disableRipple
              {...(tab.trackerId && value === index
                ? { "data-tracker": tab.trackerId }
                : {})}
              sx={{
                color: colors.GrayDarkMode[600].$value,
                textTransform: "none",
                fontWeight: typography.Font_weight.Medium.$value,
                borderRadius: spacing.Radius.md.$value,
                fontSize: typography.Size.Text.xs.$value,
                lineHeight: typography.Line_height.text.xs.$value,
                py: spacing.Radius.md.$value,
                px: spacing.Radius.xl.$value,
                minHeight: spacing.Spacing["3xl"].$value,
                minWidth:0,
                letterSpacing: "0",
                "&.Mui-selected": {
                  color: colors.Brand[600].$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                  backgroundColor: colors.Brand[50].$value + "!important",
                },
                "&:hover": { backgroundColor: "transparent" },
              }}
            />
          ))}
        </Tabs>
        <IconButton
          color="secondary"
          onClick={handleRefresh}
          disabled={!isRefreshable || isRefreshing}
          sx={{ mr: spacing.Radius.md.$value }}
        >
          <RefreshIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Box>

      {TAB_COMPONENTS.map((Component, index) => (
        <TabPanel
          key={tabConfig[index].value}
          activeIndex={value}
          index={index}
          visited={visitedTabs.has(index)}
        >
          <Component />
        </TabPanel>
      ))}
    </Box>
  );
}
